import { useState, useEffect } from 'react';
import { cmsAPI } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Save, Plus, Trash2, Star, Lock, Paintbrush, Eye, EyeOff } from 'lucide-react';
import PageBuilder from './PageBuilder';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_12492ec0-1e26-4f4b-96d9-e0a655349093/artifacts/4uh3nwhf_image.png";

export default function AdminCMS() {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [hero, setHero] = useState({});
  const [contact, setContact] = useState({});
  const [dealerInfo, setDealerInfo] = useState({});
  const [about, setAbout] = useState({});
  const [header, setHeader] = useState({});
  const [footer, setFooter] = useState({});
  const [testimonials, setTestimonials] = useState({ items: [] });
  const [saving, setSaving] = useState('');
  const [editIdx, setEditIdx] = useState(null);
  const [editForm, setEditForm] = useState({ name: '', company: '', text: '', rating: 5 });

  // Page Builder password protection
  const [builderUnlocked, setBuilderUnlocked] = useState(false);
  const [builderPwdSet, setBuilderPwdSet] = useState(null); // null = loading, true/false
  const [pwdDialog, setPwdDialog] = useState(null); // 'setup' | 'verify' | null
  const [pwdInput, setPwdInput] = useState('');
  const [pwdConfirm, setPwdConfirm] = useState('');
  const [pwdLoading, setPwdLoading] = useState(false);
  const [showPwd, setShowPwd] = useState(false);

  useEffect(() => {
    Promise.all([
      cmsAPI.get('hero'), 
      cmsAPI.get('contact'), 
      cmsAPI.get('about'), 
      cmsAPI.get('header'), 
      cmsAPI.get('testimonials'),
      cmsAPI.get('footer'),
      cmsAPI.get('dealer_info')
    ]).then(([h, c, a, hd, t, f, di]) => {
        setHero(h.data || {});
        setContact(c.data || {});
        setAbout(a.data || {});
        setHeader(hd.data || {});
        setTestimonials(t.data || { items: [] });
        setFooter(f.data || {
          description: "Canada's trusted source for quality commercial trucks and trailers. Serving the transportation industry with integrity since 2005.",
          address: "5939 Shawson Dr Suite #100\nMississauga, ON L4W 3Y2",
          phone: "(416) 417-2525",
          toll_free: "(844) 417-2525",
          email: "info@canadiantrucksales.ca",
          hours_weekday: "Mon - Fri: 9:00 AM - 6:00 PM",
          hours_saturday: "Sat: 10:00 AM - 5:00 PM",
          hours_sunday: "Sun: Closed",
        });
        setDealerInfo(di.data || {});
      })
      .catch(() => {});
  }, []);

  // Check if builder password is set
  useEffect(() => {
    const token = localStorage.getItem('cts_token');
    fetch(`${API_URL}/api/builder-password/status`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(d => setBuilderPwdSet(d.is_set))
      .catch(() => setBuilderPwdSet(false));
  }, []);

  const handleBuilderTabClick = () => {
    if (builderUnlocked) return;
    if (builderPwdSet === false) {
      setPwdDialog('setup');
    } else {
      setPwdDialog('verify');
    }
    setPwdInput('');
    setPwdConfirm('');
    setShowPwd(false);
  };

  const handleSetupPassword = async () => {
    if (pwdInput.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (pwdInput !== pwdConfirm) { toast.error('Passwords do not match'); return; }
    setPwdLoading(true);
    try {
      const token = localStorage.getItem('cts_token');
      const res = await fetch(`${API_URL}/api/builder-password/setup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: pwdInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed');
      toast.success('Builder password set!');
      setBuilderPwdSet(true);
      setBuilderUnlocked(true);
      setPwdDialog(null);
    } catch (e) { toast.error(e.message); }
    setPwdLoading(false);
  };

  const handleVerifyPassword = async () => {
    setPwdLoading(true);
    try {
      const token = localStorage.getItem('cts_token');
      const res = await fetch(`${API_URL}/api/builder-password/verify`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ password: pwdInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Incorrect password');
      setBuilderUnlocked(true);
      setPwdDialog(null);
      toast.success('Access granted');
    } catch (e) { toast.error(e.message); }
    setPwdLoading(false);
  };

  const handleResetBuilderPassword = async () => {
    if (pwdInput.length < 4) { toast.error('Password must be at least 4 characters'); return; }
    if (pwdInput !== pwdConfirm) { toast.error('Passwords do not match'); return; }
    setPwdLoading(true);
    try {
      const token = localStorage.getItem('cts_token');
      const res = await fetch(`${API_URL}/api/builder-password/reset`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ new_password: pwdInput }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || 'Failed to reset');
      toast.success('Builder password has been reset!');
      setBuilderPwdSet(true);
      setBuilderUnlocked(true);
      setPwdDialog(null);
    } catch (e) { toast.error(e.message); }
    setPwdLoading(false);
  };

  const save = async (key, data) => {
    setSaving(key);
    try { await cmsAPI.update(key, data); toast.success(`${key} updated`); } catch { toast.error('Save failed'); }
    setSaving('');
  };

  const addTestimonial = () => {
    setEditForm({ name: '', company: '', text: '', rating: 5 });
    setEditIdx(-1);
  };

  const editTestimonial = (idx) => {
    setEditForm({ ...testimonials.items[idx] });
    setEditIdx(idx);
  };

  const saveTestimonial = () => {
    const items = [...(testimonials.items || [])];
    if (editIdx === -1) {
      items.push({ ...editForm });
    } else {
      items[editIdx] = { ...editForm };
    }
    const updated = { ...testimonials, items };
    setTestimonials(updated);
    setEditIdx(null);
    save('testimonials', updated);
  };

  const deleteTestimonial = (idx) => {
    const items = [...testimonials.items];
    items.splice(idx, 1);
    const updated = { ...testimonials, items };
    setTestimonials(updated);
    save('testimonials', updated);
  };

  const logoH = header.logo_height || 48;
  const logoW = header.logo_width || 0;
  const footerH = header.footer_logo_height || 40;
  const footerW = header.footer_logo_width || 0;

  return (
    <div data-testid="admin-cms">
      <h1 className="text-2xl font-bold text-gray-900 uppercase mb-6">Content Management</h1>

      <Tabs defaultValue="header" className="space-y-4">
        <TabsList className="bg-gray-100 border-gray-200">
          <TabsTrigger value="header" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Header / Logo</TabsTrigger>
          <TabsTrigger value="footer" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Footer</TabsTrigger>
          <TabsTrigger value="hero" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Hero</TabsTrigger>
          <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Testimonials</TabsTrigger>
          <TabsTrigger value="about" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">About</TabsTrigger>
          <TabsTrigger value="dealer_info" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white">Dealer Info</TabsTrigger>
          <TabsTrigger value="builder" className="data-[state=active]:bg-[#E31937] data-[state=active]:text-white" onClick={handleBuilderTabClick} data-testid="builder-tab">
            <Lock className="w-3 h-3 mr-1" /> Page Builder
          </TabsTrigger>
        </TabsList>

        {/* Header / Logo Tab */}
        <TabsContent value="header">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader><CardTitle>Header Logo Settings</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="border border-gray-200 rounded-lg p-6 bg-gray-50" data-testid="logo-preview">
                <p className="text-xs text-gray-500 mb-3 font-medium uppercase tracking-wide">Header Logo Preview</p>
                <div className="bg-white border border-gray-200 rounded-md p-4 inline-block">
                  <img src={LOGO_URL} alt="Logo Preview" style={{ height: logoH ? `${logoH}px` : 'auto', width: logoW ? `${logoW}px` : 'auto', maxWidth: '300px' }} />
                </div>
                <p className="text-xs text-gray-400 mt-2">Current: {logoH || 'auto'}px height x {logoW || 'auto'}px width</p>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Header Logo Height (px)</label>
                  <Input type="number" value={header.logo_height || ''} onChange={e => setHeader(h => ({ ...h, logo_height: parseInt(e.target.value) || 0 }))} placeholder="48" className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-logo-height" />
                  <p className="text-xs text-gray-400 mt-1">Set 0 for auto</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Header Logo Width (px)</label>
                  <Input type="number" value={header.logo_width || ''} onChange={e => setHeader(h => ({ ...h, logo_width: parseInt(e.target.value) || 0 }))} placeholder="0 (auto)" className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-logo-width" />
                  <p className="text-xs text-gray-400 mt-1">Set 0 for auto</p>
                </div>
              </div>
              <div className="border border-gray-200 rounded-lg p-6 bg-[#0A0A0A]" data-testid="footer-logo-preview">
                <p className="text-xs text-gray-400 mb-3 font-medium uppercase tracking-wide">Footer Logo Preview</p>
                <div className="bg-white rounded-lg p-3 inline-block">
                  <img src={LOGO_URL} alt="Footer Logo" style={{ height: footerH ? `${footerH}px` : 'auto', width: footerW ? `${footerW}px` : 'auto', maxWidth: '300px' }} />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Footer Logo Height (px)</label>
                  <Input type="number" value={header.footer_logo_height || ''} onChange={e => setHeader(h => ({ ...h, footer_logo_height: parseInt(e.target.value) || 0 }))} placeholder="40" className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-footer-logo-height" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">Footer Logo Width (px)</label>
                  <Input type="number" value={header.footer_logo_width || ''} onChange={e => setHeader(h => ({ ...h, footer_logo_width: parseInt(e.target.value) || 0 }))} placeholder="0 (auto)" className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-footer-logo-width" />
                </div>
              </div>
              <Button className="bg-[#E31937] text-white" onClick={() => save('header', header)} disabled={saving === 'header'} data-testid="save-header-btn">
                <Save className="w-4 h-4 mr-1" /> {saving === 'header' ? 'Saving...' : 'Save Header Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Footer Tab */}
        <TabsContent value="footer">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader><CardTitle>Footer Content</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              {/* Description */}
              <div>
                <label className="text-xs text-gray-500 font-medium">Footer Description (under logo)</label>
                <Textarea 
                  value={footer.description || ''} 
                  onChange={e => setFooter(f => ({ ...f, description: e.target.value }))} 
                  className="bg-gray-100 border-gray-200 mt-1" 
                  rows={3}
                  placeholder="Canada's trusted source for quality commercial trucks..."
                  data-testid="cms-footer-description"
                />
              </div>

              {/* Contact Info */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Contact Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Address</label>
                    <Textarea 
                      value={footer.address || ''} 
                      onChange={e => setFooter(f => ({ ...f, address: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      rows={2}
                      placeholder="5939 Shawson Dr Suite #100&#10;Mississauga, ON L4W 3Y2"
                      data-testid="cms-footer-address"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Email</label>
                    <Input 
                      value={footer.email || ''} 
                      onChange={e => setFooter(f => ({ ...f, email: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="info@canadiantrucksales.ca"
                      data-testid="cms-footer-email"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Phone</label>
                    <Input 
                      value={footer.phone || ''} 
                      onChange={e => setFooter(f => ({ ...f, phone: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="(416) 417-2525"
                      data-testid="cms-footer-phone"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Toll-Free</label>
                    <Input 
                      value={footer.toll_free || ''} 
                      onChange={e => setFooter(f => ({ ...f, toll_free: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="(844) 417-2525"
                      data-testid="cms-footer-tollfree"
                    />
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Business Hours</h4>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Weekday Hours</label>
                    <Input 
                      value={footer.hours_weekday || ''} 
                      onChange={e => setFooter(f => ({ ...f, hours_weekday: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="Mon - Fri: 9:00 AM - 6:00 PM"
                      data-testid="cms-footer-hours-weekday"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Saturday Hours</label>
                    <Input 
                      value={footer.hours_saturday || ''} 
                      onChange={e => setFooter(f => ({ ...f, hours_saturday: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="Sat: 10:00 AM - 5:00 PM"
                      data-testid="cms-footer-hours-saturday"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Sunday Hours</label>
                    <Input 
                      value={footer.hours_sunday || ''} 
                      onChange={e => setFooter(f => ({ ...f, hours_sunday: e.target.value }))} 
                      className="bg-gray-100 border-gray-200 mt-1" 
                      placeholder="Sun: Closed"
                      data-testid="cms-footer-hours-sunday"
                    />
                  </div>
                </div>
              </div>

              <Button className="bg-[#E31937] text-white" onClick={() => save('footer', footer)} disabled={saving === 'footer'} data-testid="save-footer-btn">
                <Save className="w-4 h-4 mr-1" /> {saving === 'footer' ? 'Saving...' : 'Save Footer Settings'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Hero Tab */}
        <TabsContent value="hero">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader><CardTitle>Hero Section</CardTitle></CardHeader>
            <CardContent className="space-y-4">
              <div><label className="text-xs text-gray-500">Headline</label><Input value={hero.headline || ''} onChange={e => setHero(h => ({ ...h, headline: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-hero-headline" /></div>
              <div><label className="text-xs text-gray-500">Subheadline</label><Input value={hero.subheadline || ''} onChange={e => setHero(h => ({ ...h, subheadline: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Primary CTA</label><Input value={hero.cta_primary || ''} onChange={e => setHero(h => ({ ...h, cta_primary: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Secondary CTA</label><Input value={hero.cta_secondary || ''} onChange={e => setHero(h => ({ ...h, cta_secondary: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>

              {/* Visibility Toggles — control what shows on the public homepage */}
              <div className="border border-gray-200 rounded-md p-3 bg-gray-50">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">Public Visibility</span>
                  <span className="text-[10px] text-gray-400">Toggle items shown on the homepage hero</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {[
                    { k: 'show_headline', label: 'Show Headline' },
                    { k: 'show_subheadline', label: 'Show Subheadline' },
                    { k: 'show_cta_primary', label: 'Show Primary Button' },
                    { k: 'show_cta_secondary', label: 'Show Secondary Button' },
                  ].map(({ k, label }) => (
                    <label key={k} className="flex items-center justify-between gap-2 px-2 py-1.5 bg-white rounded border border-gray-100 hover:border-gray-200 transition-colors cursor-pointer">
                      <span className="text-xs text-gray-700">{label}</span>
                      <input
                        type="checkbox"
                        checked={hero[k] !== false}
                        onChange={e => setHero(h => ({ ...h, [k]: e.target.checked }))}
                        className="accent-[#E31937] h-4 w-4"
                        data-testid={`cms-hero-${k.replace(/_/g, '-')}`}
                      />
                    </label>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-gray-200">
                  <label className="text-[11px] text-gray-500 uppercase block mb-1">Image Display Mode</label>
                  <select
                    value={hero.display_mode || 'auto'}
                    onChange={e => setHero(h => ({ ...h, display_mode: e.target.value }))}
                    className="w-full h-9 px-2 text-sm bg-white border border-gray-200 rounded"
                    data-testid="cms-hero-display-mode"
                  >
                    <option value="auto">Auto — best fit (recommended)</option>
                    <option value="cover">Cover — fill section (may crop edges)</option>
                    <option value="contain">Contain — full image (may add bars)</option>
                  </select>
                  <p className="text-[10px] text-gray-400 mt-1">
                    {hero.display_mode === 'cover'
                      ? 'Image fills the entire hero — no bars, edges may be cropped to fit.'
                      : hero.display_mode === 'contain'
                      ? 'Full image always visible — empty space filled with dark background.'
                      : 'Auto-fits the image cleanly: fills if aspect matches, otherwise shows the full image with subtle bars.'}
                  </p>
                </div>

                <div className="mt-3 pt-3 border-t border-gray-200">
                  <span className="text-[11px] text-gray-500 uppercase block mb-2">Hero Section Height</span>
                  {[
                    { k: 'height_desktop', label: 'Desktop', min: 300, max: 900, def: 460 },
                    { k: 'height_tablet', label: 'Tablet', min: 280, max: 800, def: 380 },
                    { k: 'height_mobile', label: 'Mobile', min: 240, max: 800, def: 320 },
                  ].map(({ k, label, min, max, def }) => {
                    const val = Number(hero[k]) || def;
                    return (
                      <div key={k} className="mb-2">
                        <div className="flex items-center justify-between text-[11px] text-gray-600">
                          <span>{label}</span>
                          <span className="font-mono text-gray-500">{val} px</span>
                        </div>
                        <input
                          type="range"
                          min={min}
                          max={max}
                          step={10}
                          value={val}
                          onChange={e => setHero(h => ({ ...h, [k]: Number(e.target.value) }))}
                          className="w-full accent-[#E31937]"
                          data-testid={`cms-hero-${k.replace(/_/g, '-')}`}
                        />
                      </div>
                    );
                  })}
                  <p className="text-[10px] text-gray-400 mt-1">Drag to set hero height per breakpoint. Defaults: 460 / 380 / 320 px.</p>
                </div>
              </div>

              {/* Hero Image */}
              <div>
                <label className="text-xs text-gray-500">Desktop Hero Image</label>
                <p className="text-[10px] text-gray-400 mb-1">Recommended: <strong>1920 × 700px</strong> (landscape). Use JPG/WebP for best performance.</p>
                <div className="mt-1 space-y-2">
                  <Input value={hero.image || ''} onChange={e => setHero(h => ({ ...h, image: e.target.value }))} placeholder="Paste image URL..." className="bg-gray-100 border-gray-200" data-testid="cms-hero-image-url" />
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">OR</span>
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded text-xs text-gray-700 transition-colors">
                      <Plus className="w-3 h-3" /> Upload from Device
                      <input type="file" accept="image/*" className="hidden" data-testid="cms-hero-image-upload" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await fetch(`${API_URL}/api/upload/hero`, { method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${localStorage.getItem('cts_token')}` } });
                          const data = await res.json();
                          if (data.url) {
                            setHero(h => ({ ...h, image: `${API_URL}${data.url}` }));
                            toast.success('Image uploaded');
                          }
                        } catch { toast.error('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                  </div>

                  {/* Image Fit Control */}
                  <div className="flex items-center gap-3 mt-2">
                    <label className="text-xs text-gray-500 w-20">Image Fit</label>
                    <select value={hero.image_fit || 'cover'} onChange={e => setHero(h => ({ ...h, image_fit: e.target.value }))}
                      className="h-8 px-2 text-xs bg-gray-100 border border-gray-200 rounded" data-testid="cms-hero-image-fit">
                      <option value="cover">Cover (fill & crop)</option>
                      <option value="contain">Contain (fit whole image)</option>
                      <option value="fill">Stretch to fill</option>
                    </select>
                    <span className="text-[10px] text-gray-400">
                      {hero.image_fit === 'contain' ? 'Full image visible, may show background color' : hero.image_fit === 'fill' ? 'Stretches to fill, may distort' : 'Fills section, crops edges if needed'}
                    </span>
                  </div>

                  {/* Image Position */}
                  <div className="flex items-center gap-3">
                    <label className="text-xs text-gray-500 w-20">Position</label>
                    <select value={hero.image_position || 'center'} onChange={e => setHero(h => ({ ...h, image_position: e.target.value }))}
                      className="h-8 px-2 text-xs bg-gray-100 border border-gray-200 rounded" data-testid="cms-hero-image-position">
                      <option value="center">Center</option>
                      <option value="top">Top</option>
                      <option value="bottom">Bottom</option>
                      <option value="left">Left</option>
                      <option value="right">Right</option>
                    </select>
                    <span className="text-[10px] text-gray-400">Focal point of the image</span>
                  </div>

                  {hero.image && (
                    <div className="relative rounded overflow-hidden border border-gray-200 mt-2" style={{ height: '180px', backgroundColor: '#1a1a1a' }}>
                      <img src={hero.image} alt="Hero preview" style={{ width: '100%', height: '100%', objectFit: hero.image_fit || 'cover', objectPosition: hero.image_position || 'center' }} />
                      <div className="absolute inset-0" style={{ backgroundColor: `rgba(10, 10, 10, ${(hero.overlay_opacity ?? 70) / 100})` }} />
                      <span className="absolute bottom-2 left-2 text-white text-[10px] font-medium bg-black/40 px-2 py-0.5 rounded">Preview with overlay</span>
                      <span className="absolute bottom-2 right-2 text-white text-[10px] font-medium bg-black/40 px-2 py-0.5 rounded">1920 × 700</span>
                    </div>
                  )}
                </div>
              </div>

              {/* === Tablet Hero Image === */}
              <div>
                <label className="text-xs text-gray-500">Tablet Hero Image <span className="text-gray-400 font-normal">(optional)</span></label>
                <p className="text-[10px] text-gray-400 mb-1">Recommended: <strong>1280 × 800px</strong>. Leave blank to fall back to the desktop image.</p>
                <div className="mt-1 space-y-2">
                  <Input value={hero.image_tablet || ''} onChange={e => setHero(h => ({ ...h, image_tablet: e.target.value }))} placeholder="Paste image URL..." className="bg-gray-100 border-gray-200" data-testid="cms-hero-image-tablet-url" />
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">OR</span>
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded text-xs text-gray-700 transition-colors">
                      <Plus className="w-3 h-3" /> Upload Tablet Image
                      <input type="file" accept="image/*" className="hidden" data-testid="cms-hero-image-tablet-upload" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await fetch(`${API_URL}/api/upload/hero`, { method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${localStorage.getItem('cts_token')}` } });
                          const data = await res.json();
                          if (data.url) {
                            setHero(h => ({ ...h, image_tablet: `${API_URL}${data.url}` }));
                            toast.success('Tablet image uploaded');
                          }
                        } catch { toast.error('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                    {hero.image_tablet && (
                      <button onClick={() => setHero(h => ({ ...h, image_tablet: '' }))} className="text-[10px] text-red-500 hover:underline" data-testid="cms-hero-image-tablet-clear">Clear</button>
                    )}
                  </div>
                  {hero.image_tablet && (
                    <div className="relative rounded overflow-hidden border border-gray-200 mt-2" style={{ height: '160px', backgroundColor: '#1a1a1a', maxWidth: '420px' }}>
                      <img src={hero.image_tablet} alt="Tablet preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span className="absolute bottom-2 right-2 text-white text-[10px] font-medium bg-black/40 px-2 py-0.5 rounded">1280 × 800</span>
                    </div>
                  )}
                </div>
              </div>

              {/* === Mobile Hero Image === */}
              <div>
                <label className="text-xs text-gray-500">Mobile Hero Image <span className="text-gray-400 font-normal">(optional, portrait)</span></label>
                <p className="text-[10px] text-gray-400 mb-1">Recommended: <strong>1080 × 1350px</strong> or <strong>900 × 1200px</strong> (portrait). Leave blank to fall back to the desktop image with safe crop.</p>
                <div className="mt-1 space-y-2">
                  <Input value={hero.image_mobile || ''} onChange={e => setHero(h => ({ ...h, image_mobile: e.target.value }))} placeholder="Paste image URL..." className="bg-gray-100 border-gray-200" data-testid="cms-hero-image-mobile-url" />
                  <div className="flex items-center gap-2">
                    <span className="text-[10px] text-gray-400">OR</span>
                    <label className="cursor-pointer inline-flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 border border-gray-200 rounded text-xs text-gray-700 transition-colors">
                      <Plus className="w-3 h-3" /> Upload Mobile Image
                      <input type="file" accept="image/*" className="hidden" data-testid="cms-hero-image-mobile-upload" onChange={async (e) => {
                        const file = e.target.files?.[0];
                        if (!file) return;
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await fetch(`${API_URL}/api/upload/hero`, { method: 'POST', body: formData, headers: { 'Authorization': `Bearer ${localStorage.getItem('cts_token')}` } });
                          const data = await res.json();
                          if (data.url) {
                            setHero(h => ({ ...h, image_mobile: `${API_URL}${data.url}` }));
                            toast.success('Mobile image uploaded');
                          }
                        } catch { toast.error('Upload failed'); }
                        e.target.value = '';
                      }} />
                    </label>
                    {hero.image_mobile && (
                      <button onClick={() => setHero(h => ({ ...h, image_mobile: '' }))} className="text-[10px] text-red-500 hover:underline" data-testid="cms-hero-image-mobile-clear">Clear</button>
                    )}
                  </div>
                  {hero.image_mobile && (
                    <div className="relative rounded overflow-hidden border border-gray-200 mt-2" style={{ height: '280px', width: '180px', backgroundColor: '#1a1a1a' }}>
                      <img src={hero.image_mobile} alt="Mobile preview" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      <span className="absolute bottom-2 right-2 text-white text-[10px] font-medium bg-black/40 px-2 py-0.5 rounded">1080 × 1350</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Shadow/Overlay Control */}
              <div>
                <label className="text-xs text-gray-500">Shadow / Overlay Darkness</label>
                <p className="text-[10px] text-gray-400 mb-1">Controls how dark the overlay is on the hero image (0 = no shadow, 100 = fully dark)</p>
                <div className="flex items-center gap-3">
                  <input
                    type="range"
                    min="0" max="100" step="5"
                    value={hero.overlay_opacity ?? 70}
                    onChange={e => setHero(h => ({ ...h, overlay_opacity: parseInt(e.target.value) }))}
                    className="flex-1 h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-[#E31937]"
                    data-testid="cms-hero-overlay-slider"
                  />
                  <span className="text-sm font-medium text-gray-700 w-10 text-right">{hero.overlay_opacity ?? 70}%</span>
                </div>
                <div className="flex justify-between text-[10px] text-gray-400 mt-0.5">
                  <span>Light</span>
                  <span>Dark</span>
                </div>
              </div>

              <Button className="bg-[#E31937] text-white" onClick={() => save('hero', hero)} disabled={saving === 'hero'} data-testid="save-hero-btn">
                <Save className="w-4 h-4 mr-1" /> {saving === 'hero' ? 'Saving...' : 'Save Hero'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Testimonials Tab */}
        <TabsContent value="testimonials">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>Client Testimonials</CardTitle>
                <Button className="bg-[#E31937] text-white" size="sm" onClick={addTestimonial} data-testid="add-testimonial-btn">
                  <Plus className="w-4 h-4 mr-1" /> Add Testimonial
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {(testimonials.items || []).length === 0 ? (
                <p className="text-gray-400 text-sm text-center py-8">No testimonials yet. Click "Add Testimonial" to create one.</p>
              ) : (
                <div className="space-y-3">
                  {testimonials.items.map((t, i) => (
                    <div key={`testimonial-${t.name}-${i}`} className="border border-gray-200 rounded-lg p-4 flex items-start justify-between gap-4" data-testid={`testimonial-item-${i}`}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <span className="font-semibold text-sm">{t.name}</span>
                          {t.company && <span className="text-gray-400 text-xs">- {t.company}</span>}
                        </div>
                        <div className="flex gap-0.5 mb-2">
                          {[...Array(5)].map((_, s) => (
                            <Star key={s} className={`w-3.5 h-3.5 ${s < (t.rating || 5) ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                          ))}
                        </div>
                        <p className="text-gray-600 text-sm leading-relaxed">"{t.text}"</p>
                      </div>
                      <div className="flex gap-1 flex-shrink-0">
                        <Button size="sm" variant="outline" className="h-8 px-2 text-xs" onClick={() => editTestimonial(i)}>Edit</Button>
                        <Button size="sm" variant="outline" className="h-8 px-2 text-xs text-red-500 border-red-200 hover:bg-red-50" onClick={() => deleteTestimonial(i)}>
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Edit Dialog */}
          <Dialog open={editIdx !== null} onOpenChange={() => setEditIdx(null)}>
            <DialogContent className="bg-white border-gray-200 text-gray-900">
              <DialogHeader><DialogTitle className="text-gray-900">{editIdx === -1 ? 'Add Testimonial' : 'Edit Testimonial'}</DialogTitle></DialogHeader>
              <div className="space-y-3">
                <div><label className="text-xs text-gray-500">Client Name *</label><Input value={editForm.name} onChange={e => setEditForm(f => ({ ...f, name: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="John Smith" data-testid="testimonial-name" /></div>
                <div><label className="text-xs text-gray-500">Company</label><Input value={editForm.company} onChange={e => setEditForm(f => ({ ...f, company: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="Smith Logistics" /></div>
                <div>
                  <label className="text-xs text-gray-500">Rating</label>
                  <div className="flex gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map(s => (
                      <button key={s} type="button" onClick={() => setEditForm(f => ({ ...f, rating: s }))} className="p-0.5">
                        <Star className={`w-6 h-6 ${s <= editForm.rating ? 'text-amber-400 fill-amber-400' : 'text-gray-300'}`} />
                      </button>
                    ))}
                  </div>
                </div>
                <div><label className="text-xs text-gray-500">Review Text *</label><Textarea value={editForm.text} onChange={e => setEditForm(f => ({ ...f, text: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={3} placeholder="CTS helped us find..." data-testid="testimonial-text" /></div>
                <div className="flex gap-2 justify-end">
                  <Button variant="outline" className="border-gray-300" onClick={() => setEditIdx(null)}>Cancel</Button>
                  <Button className="bg-[#E31937] text-white" onClick={saveTestimonial} disabled={!editForm.name || !editForm.text} data-testid="save-testimonial-btn">Save</Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* About Tab */}
        <TabsContent value="about">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader><CardTitle>About Page</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><label className="text-xs text-gray-500">Title</label><Input value={about.title || ''} onChange={e => setAbout(a => ({ ...a, title: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" /></div>
              <div><label className="text-xs text-gray-500">Content</label><Textarea value={about.content || ''} onChange={e => setAbout(a => ({ ...a, content: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" rows={8} /></div>
              <Button className="bg-[#E31937] text-white" onClick={() => save('about', about)} disabled={saving === 'about'}>
                <Save className="w-4 h-4 mr-1" /> {saving === 'about' ? 'Saving...' : 'Save About'}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Dealer Info Tab (replaces old Contact tab) */}
        <TabsContent value="dealer_info">
          <Card className="bg-white border-gray-200 text-gray-900">
            <CardHeader><CardTitle>Dealer Information</CardTitle></CardHeader>
            <CardContent className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-gray-500 font-medium">Dealer Licence Number</label>
                  <Input value={dealerInfo.dealer_number || ''} onChange={e => setDealerInfo(d => ({ ...d, dealer_number: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="Dealer Lic# 4020193" data-testid="cms-dealer-number" />
                  <p className="text-xs text-gray-400 mt-1">Used on Bill of Sale documents</p>
                </div>
                <div>
                  <label className="text-xs text-gray-500 font-medium">HST Number</label>
                  <Input value={dealerInfo.hst_number || ''} onChange={e => setDealerInfo(d => ({ ...d, hst_number: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" placeholder="HST# 723916329RT0001" data-testid="cms-hst-number" />
                  <p className="text-xs text-gray-400 mt-1">Used on Bill of Sale and Invoice documents</p>
                </div>
              </div>
              <div className="border-t border-gray-200 pt-4">
                <h4 className="text-sm font-semibold text-gray-700 mb-4">Contact Information</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Phone</label>
                    <Input value={contact.phone || ''} onChange={e => setContact(c => ({ ...c, phone: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-contact-phone" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Email</label>
                    <Input value={contact.email || ''} onChange={e => setContact(c => ({ ...c, email: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-contact-email" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Address</label>
                    <Input value={contact.address || ''} onChange={e => setContact(c => ({ ...c, address: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-contact-address" />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 font-medium">Hours</label>
                    <Input value={contact.hours || ''} onChange={e => setContact(c => ({ ...c, hours: e.target.value }))} className="bg-gray-100 border-gray-200 mt-1" data-testid="cms-contact-hours" />
                  </div>
                </div>
              </div>
              <div className="flex gap-2">
                <Button className="bg-[#E31937] text-white" onClick={async () => { await save('dealer_info', dealerInfo); await save('contact', contact); }} disabled={saving === 'dealer_info' || saving === 'contact'}>
                  <Save className="w-4 h-4 mr-1" /> {saving === 'dealer_info' || saving === 'contact' ? 'Saving...' : 'Save Dealer Info'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Page Builder Tab */}
        <TabsContent value="builder" className="mt-0">
          {builderUnlocked ? (
            <div className="-mx-6 -mb-6" style={{ height: 'calc(100vh - 160px)' }}>
              <PageBuilder />
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-20 text-center">
              <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                <Lock className="w-8 h-8 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-800">Page Builder is Password Protected</h3>
              <p className="text-sm text-gray-500 mt-1 max-w-sm">
                {builderPwdSet === false
                  ? 'Set up a password to protect the page builder from unauthorized changes.'
                  : 'Enter the builder password to access the visual editor.'}
              </p>
              <Button
                className="mt-6 bg-[#E31937] text-white hover:bg-[#C4122C]"
                onClick={handleBuilderTabClick}
                data-testid="unlock-builder-btn"
              >
                <Lock className="w-4 h-4 mr-2" /> {builderPwdSet === false ? 'Set Password' : 'Unlock Builder'}
              </Button>
            </div>
          )}
        </TabsContent>

        {/* Password Setup Dialog */}
        <Dialog open={pwdDialog === 'setup'} onOpenChange={() => setPwdDialog(null)}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-gray-900">
                <Lock className="w-5 h-5 text-[#E31937]" /> Set Builder Password
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">This password will be required every time someone accesses the Page Builder.</p>
            <div className="space-y-3 mt-2">
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="Enter password (min 4 chars)"
                  value={pwdInput}
                  onChange={e => setPwdInput(e.target.value)}
                  className="pr-10"
                  data-testid="builder-pwd-input"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Input
                type={showPwd ? 'text' : 'password'}
                placeholder="Confirm password"
                value={pwdConfirm}
                onChange={e => setPwdConfirm(e.target.value)}
                data-testid="builder-pwd-confirm"
              />
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setPwdDialog(null)}>Cancel</Button>
              <Button className="bg-[#E31937] text-white" onClick={handleSetupPassword} disabled={pwdLoading} data-testid="set-pwd-btn">
                {pwdLoading ? 'Setting...' : 'Set Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Verify Dialog */}
        <Dialog open={pwdDialog === 'verify'} onOpenChange={() => setPwdDialog(null)}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-gray-900">
                <Lock className="w-5 h-5 text-[#E31937]" /> Enter Builder Password
              </DialogTitle>
            </DialogHeader>
            <div className="relative mt-2">
              <Input
                type={showPwd ? 'text' : 'password'}
                placeholder="Enter builder password"
                value={pwdInput}
                onChange={e => setPwdInput(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleVerifyPassword()}
                className="pr-10"
                data-testid="builder-verify-input"
              />
              <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
                {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
            {isAdmin && (
              <button type="button" className="text-xs text-[#E31937] hover:underline mt-1" onClick={() => { setPwdDialog('reset'); setPwdInput(''); setPwdConfirm(''); setShowPwd(false); }} data-testid="forgot-builder-pwd">
                Forgot password? Reset it
              </button>
            )}
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setPwdDialog(null)}>Cancel</Button>
              <Button className="bg-[#E31937] text-white" onClick={handleVerifyPassword} disabled={pwdLoading} data-testid="verify-pwd-btn">
                {pwdLoading ? 'Verifying...' : 'Unlock'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Password Reset Dialog (Admin only) */}
        <Dialog open={pwdDialog === 'reset'} onOpenChange={() => setPwdDialog(null)}>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-gray-900">
                <Lock className="w-5 h-5 text-[#E31937]" /> Reset Builder Password
              </DialogTitle>
            </DialogHeader>
            <p className="text-sm text-gray-500">Set a new builder password. This will replace the existing one.</p>
            <div className="space-y-3 mt-2">
              <div className="relative">
                <Input
                  type={showPwd ? 'text' : 'password'}
                  placeholder="New password (min 4 chars)"
                  value={pwdInput}
                  onChange={e => setPwdInput(e.target.value)}
                  className="pr-10"
                  data-testid="builder-reset-input"
                />
                <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" onClick={() => setShowPwd(!showPwd)}>
                  {showPwd ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <Input
                type={showPwd ? 'text' : 'password'}
                placeholder="Confirm new password"
                value={pwdConfirm}
                onChange={e => setPwdConfirm(e.target.value)}
                data-testid="builder-reset-confirm"
              />
            </div>
            <DialogFooter className="gap-2 mt-2">
              <Button variant="outline" onClick={() => setPwdDialog(null)}>Cancel</Button>
              <Button className="bg-[#E31937] text-white" onClick={handleResetBuilderPassword} disabled={pwdLoading} data-testid="reset-pwd-btn">
                {pwdLoading ? 'Resetting...' : 'Reset Password'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tabs>
    </div>
  );
}
